import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import { db } from "~/api/db";
import {
  requireText,
  rootKey,
  workspaceKey,
  type WorkspaceInvitation,
  type WorkspaceMember,
  type WorkspaceRole,
} from "./shared";

export function useWorkspaceTeam(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("team", workspaceId),
    queryFn: async () => {
      if (!workspaceId) {
        return {
          members: [] as WorkspaceMember[],
          invitations: [] as WorkspaceInvitation[],
        };
      }

      const [
        { data: members, error: membersError },
        { data: invitations, error: invitationsError },
      ] = await Promise.all([
        db
          .from("workspace_members")
          .select("*")
          .eq("workspace_id", workspaceId)
          .order("created_at", { ascending: true }),
        db
          .from("workspace_invitations")
          .select("*")
          .eq("workspace_id", workspaceId)
          .order("created_at", { ascending: false }),
      ]);

      if (membersError) throw membersError;
      if (invitationsError) throw invitationsError;

      return {
        members: (members ?? []) as WorkspaceMember[],
        invitations: (invitations ?? []) as WorkspaceInvitation[],
      };
    },
    enabled: Boolean(workspaceId),
  });

  const invite = useMutation({
    mutationFn: async (input: { email: string; role: WorkspaceRole }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const email = requireText(input.email, "Email");
      const invitation = await unwrap(
        api.inviteToWorkspace({
          body: { workspace_id: workspaceId, email, role: input.role },
        }),
      );
      return { invitation };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("team", workspaceId),
      });
    },
  });

  const resend = useMutation({
    mutationFn: async (invitationId: string) => {
      const invitation = await unwrap(
        api.resendWorkspaceInvitation({ path: { id: invitationId } }),
      );
      return { invitation };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("team", workspaceId),
      });
    },
  });

  const revoke = useMutation({
    mutationFn: async (invitationId: string) => {
      const invitation = await unwrap(
        api.revokeWorkspaceInvitation({ path: { id: invitationId } }),
      );
      return { invitation };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("team", workspaceId),
      });
    },
  });

  const updateMemberRole = useMutation({
    mutationFn: async (input: { memberId: string; role: WorkspaceRole }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { data, error } = await db
        .from("workspace_members")
        .update({ role: input.role })
        .eq("workspace_id", workspaceId)
        .eq("id", input.memberId)
        .select()
        .single();
      if (error) throw error;
      return data as WorkspaceMember;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("team", workspaceId),
      });
    },
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("workspace_members")
        .delete()
        .eq("workspace_id", workspaceId)
        .eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("team", workspaceId),
      });
    },
  });

  return {
    ...query,
    members: query.data?.members ?? [],
    invitations: query.data?.invitations ?? [],
    invite,
    removeMember,
    resend,
    revoke,
    updateMemberRole,
  };
}
